import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { PatientAppointmentHistoryService } from './patient-appointment-history.service';
import { MatTableDataSource } from '@angular/material/table';
import { PatientAppointmentHistory } from './patient-appointment-history.type';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { TablerIconsModule } from 'angular-tabler-icons';
import { SelectionService } from 'src/app/services/selection.service';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../../user/user.service';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { IUsers } from '../../user/iusers';
import { Router } from '@angular/router';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { environment } from 'src/environments/environment';
import { isTodayOrInThePast } from 'src/app/common/function-helper';
import { OPDService } from '../../opd/opd.service';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { ToastrService } from 'ngx-toastr';
import { AddBillChargeComponent } from '../../dialogs/bill/bill.component';
import { BillDialogData } from '../../dialogs/bill/bill-dialog-data.interface';
import { BillType } from 'src/app/common/enums/bill-type';
import { AppointmentStatusType } from 'src/app/common/enums/appointment-status-type';
import { AppointmentDialog } from '../../dialogs/confirmation-appointment/appointment-dialog.component';
import { PatientService } from '../../patient/patient.service';
import { IPatientInfo } from '../../patient/ipatient-info';
import { DeleteDialog } from '../../dialogs/delete.component';
import { AppointmentService } from '../appointment.service';
import { LoginService } from 'src/app/login.service';
import { PatientReportViewerComponent } from '../../dialogs/patient-report-print/patient-report-viewer/patient-report-viewer.component';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { PrescriptionService } from '../../opd/prescription/prescription.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';


@Component({
  selector: 'app-patient-appointment-history',
  imports: [
    MaterialModule,
    CommonModule,
    TranslateModule,
    TimeFormatPipe,
    TablerIconsModule
  ],
  providers: [
    TimeFormatPipe,
    DatePipe,
    DefaultValuePipe
  ],
  templateUrl: './patient-appointment-history.component.html',
  styleUrl: './patient-appointment-history.component.scss'
})
export class PatientAppointmentHistoryComponent implements OnInit, OnDestroy {
  dataSource = new MatTableDataSource<PatientAppointmentHistory>();
  @Input() patientId!: string | number | undefined;
  @Input() showOPD: boolean;
  @Output() patientAppointmenthistoryLength = new EventEmitter<number>();
  displayedColumns: string[] = ['appointDate', 'appointTime', 'appointmentTypeName', 'OPDNO', 'status', 'Charge', 'viewDetails'];
  showPatientInfo = false;
  printTitle: string = '';
  doctorInfo: IUsers;
  patientInfo: IPatientInfo;
  hospitalInfo: IHospitalInfo;
  patientFullName: string = '';
  doctorId: number;
  staticimagepath = environment.imagePath;
  isIPD: boolean = false;
  isLoading: boolean = true;
  private destroy$ = new Subject<void>();
  isDialogMode: boolean = false;

  constructor(
    private patientAppointmentHistoryService: PatientAppointmentHistoryService,
    @Inject(MAT_DIALOG_DATA) private _data: { patientId: number, patientName: string, isDialog: boolean },
    private selectionService: SelectionService,
    private datePipe: DatePipe,
    private http: HttpClient,
    private _userService: UserService,
    private timeFormatPipe: TimeFormatPipe,
    private dialogRef: MatDialogRef<PatientAppointmentHistoryComponent>,
    private _router: Router,
    private hospitalState: HospitalStateService,
    private _OPDService: OPDService,
    private translationUtils: TranslationUtils,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private _patientService: PatientService,
    private _appointmentService: AppointmentService,
    private loginservice: LoginService,
    private printSettingService: PrintSettingService,
    private defaultValuePipe: DefaultValuePipe,
    private cdr: ChangeDetectorRef,
    private prescriptionService: PrescriptionService
  ) { }

  ngOnInit(): void {
    this.isIPD = this.loginservice.isIPDTabShown();
    this.isDialogMode = this._data?.isDialog || false;
    const patientId = this._data?.patientId || Number(this.patientId);

    forkJoin({
      patient: this._patientService.getPatientByID(patientId),
      history: this.patientAppointmentHistoryService.getPatientHistory(patientId)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ patient, history }) => {
        this.patientInfo = patient.result;
        this.patientFullName = [this.patientInfo.firstName, this.patientInfo.lastName]
          .filter(Boolean)
          .join(' ');

        this.printTitle = `Appointment History of - ${this.patientFullName}`;

        this.setHistory(history.result);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setHistory(history: PatientAppointmentHistory[]) {
    this.dataSource.data = history || [];
    this.patientAppointmenthistoryLength.emit(this.dataSource.data.length);

    if (history?.length) {
      this._userService
        .getUserByID(history[0].doctorId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => this.doctorInfo = res.result);
    }

    this.hospitalInfo = this.hospitalState.getHospital();
    this.checkReportsForHistory(this.dataSource.data);
    this.isLoading = false;
  }

  private checkReportsForHistory(history: PatientAppointmentHistory[]) {
    history.forEach(app => {
      const opdId = app?.opdId;
      if (app && opdId != null) {
        forkJoin({
          report: this._OPDService.getPatientReport(Number(opdId)),
          prescription: this.prescriptionService.getPrescriptionDetailByOpdId(Number(opdId))
        })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              const hasReportData = (res.report?.result?.length > 0);
              const pres = res.prescription?.result;
              const hasPrescriptionData = Array.isArray(pres) ? pres.length > 0 : !!pres;

              app.hasReport = hasReportData || hasPrescriptionData;
              this.cdr.markForCheck();
            },
            error: () => {
              app.hasReport = false;
              this.cdr.markForCheck();
            }
          });
      }
    });
  }

  viewDetails(element: PatientAppointmentHistory) {
    const data = {
      doctorId: element.doctorId.toString(),
      appointmentDate: element.appointDate,
      appointmentId: element.id
    };

    this.selectionService.set(data);
    this._router.navigate(['/appointment']);
    this.dialogRef.close(true);
  }

  async printHistory() {
    const printContent = await this.createPrintContent();
    this.openPrintWindow(printContent);
  }

  private async createPrintContent(): Promise<string> {
    let template =
      (await this.http
        .get('assets/print-template.html', { responseType: 'text' })
        .toPromise()) || '';

    if (!this.showPatientInfo) {
      template = template.replace(
        '</head>',
        `<style>
        #showPatientInfo, #patientInfo { display: none !important; }
      </style></head>`
      );
    }
    const totalCharge = (this.dataSource.data || []).reduce((sum, app) => {
      const value = Number(app.charge);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);

    // Build div-based appointment "table"
    const rowsHtml = this.dataSource.data
      .map(
        (app) => `
      <div class="row" style="display:flex; text-align:center; border-bottom:1px solid #ccc; padding:6px 0;">
        <div style="flex:1;">${this.datePipe.transform(app.appointDate, 'dd-MM-yyyy')}</div>
        <div style="flex:1;">${this.timeFormatPipe.transform(app.appointTime)}</div>
        <div style="flex:2;">${app.appointmentTypeName ?? ''}</div>
        <div style="flex:1;">${app.opdNumber ?? ''}</div>
        <div style="flex:1;">${app.status ?? ''}</div>
        <div style="flex:1;">${app.charge ?? ''}</div>
      </div>`
      )
      .join('');

    const tableHtml = `
    <div style="text-align:center; margin-bottom:10px;">
      <h2>Appointment History of - ${this.patientFullName}</h2>
    </div>

    <div class="report-table" style="display:flex; flex-direction:column; border:1px solid #999; border-radius:6px; overflow:hidden; font-size:14px;">
      
      <!-- Header -->
      <div class="header" style="display:flex; background:#f2f2f2; font-weight:600; text-align:center; border-bottom:1px solid #ccc; padding:6px 0;">
        <div style="flex:1;">Date</div>
        <div style="flex:1;">Time</div>
        <div style="flex:2;">Appointment Type</div>
        <div style="flex:1;">OPD No</div>
        <div style="flex:1;">Status</div>
        <div style="flex:1;">Charge</div>
      </div>

      <!-- Rows -->
      ${rowsHtml}
    </div>

    <div style="text-align:right; font-weight:600; margin-top:6px;">
      Total Charge: ${totalCharge}
    </div>
  `;

    const hospitalSafe = this.hospitalState.getHospital();
    const fullImagePath = hospitalSafe?.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
    const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;
    const settings = await this.loginservice.getHospitalId() ? await this.printSettingService.getPrintSettingPromise(Number(this.loginservice.getHospitalId())) : null;

    const replacements: Record<string, string> = {
      reportTitle: this.printTitle,
      hospitalName: this.defaultValuePipe.transform(hospitalSafe?.name, ''),
      hospitalAddress: this.defaultValuePipe.transform(hospitalSafe?.address, '') + (hospitalSafe?.city ? ' ,' + this.defaultValuePipe.transform(hospitalSafe.city, '') : ''),
      phone: this.defaultValuePipe.transform(hospitalSafe?.phone, ''),
      regNo: this.defaultValuePipe.transform(hospitalSafe?.registrationNumber, ''),
      patientName: this.patientFullName,
      patientAge: this.defaultValuePipe.transform(this.patientInfo?.age, ''),
      opdNumber: this.defaultValuePipe.transform(this.dataSource.data[0]?.opdNumber, ''),
      opdDate: this.defaultValuePipe.transform(this.dataSource.data[0]?.appointDate, 'dd/MM/yyyy'),
      reportSections: tableHtml,
      doctorName: ((this.doctorInfo?.firstName ?? '') + ' ' + (this.doctorInfo?.lastName ?? '')).trim() || '',
      qualification: this.doctorInfo?.degree ?? '',
      doctorRegistrationNo: this.doctorInfo?.registrationNumber 
        ? `Reg No. ${this.defaultValuePipe.transform(this.doctorInfo?.registrationNumber, '')}` 
        : '',
      doctorPhone: this.doctorInfo?.phoneNumber 
        ? `M. ${this.defaultValuePipe.transform(this.doctorInfo?.phoneNumber, '')}` 
        : '',
      logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`
    };

    let filledTemplate = template;
    for (const key in replacements) {
      const value = replacements[key] ?? '';
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      filledTemplate = filledTemplate.replace(regex, value);
    }

    // Apply settings
    if (settings) {
      const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
      const headerClass = settings.logoPosition === 'center' ? 'center' : '';
      const headerAlignment = settings.addressAlignment || 'left';

      const styles = `
          :root {
              --logo-size: ${settings.logoSize || '60px'};
              --logo-position: ${logoPos};
              --header-alignment: ${headerAlignment};
              --hospital-name-size: ${settings.hospitalNameSize || 20}px;
              --address-font-size: ${settings.addressFontSize || 13}px;
              --footer-display: ${settings.showFooter ? 'block' : 'none'};
              --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
              --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
          }
      `;
      filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
    } else {
      filledTemplate = filledTemplate.replace('{{headerClass}}', '');
    }

    return filledTemplate;
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) return;

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }

  isTodayOrInThePast(date: Date): boolean {
    return isTodayOrInThePast(date);
  }

  StartConsultation(element: PatientAppointmentHistory) {
    const opdCreateData = {
      patientId: element.patientId,
      opdDate: element.appointDate,
      doctorId: element.doctorId,
      appointmentId: element.id,
    }
    this.selectionService.set({
      doctorId: element.doctorId.toString(),
      appointmentDate: element.appointDate.toString()
    });
    if (!element.opdNumber) {
      this._OPDService.createOPD(opdCreateData).subscribe((response) => {
        if (response.isSuccess) {
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.OPD')));
          this._router.navigate(['/opd', element.patientId, 'consultation', response.result.id]);
        } else {
          const allErrors = Object.values(response.errors || {}).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      });
    } else {
      this._router.navigate(['/opd', element.patientId, 'consultation', element.opdId]);
    }
    this.dialogRef.close(true);
  }

  OnBillChargeAdd(element: PatientAppointmentHistory) {
    const statusNumber = AppointmentStatusType[element?.status as any as keyof typeof AppointmentStatusType];
    if (statusNumber !== AppointmentStatusType.InProgress) {
      this.openBillChargeDialog(element);
      return;
    }

    const dialogRef = this.dialog.open(AppointmentDialog, {
      width: '400px',
      data: {
        message: this.translate.instant('APPOINTMENT_RELATED.PRINT_BILL_CONFIRMATION'),
        title: this.translate.instant('ENTITY.APPOINTMENT')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'yes') {
        this.openBillChargeDialog(element);
      }
    });
  }

  private openBillChargeDialog(element: PatientAppointmentHistory) {
    const opdBillWithPatientName = {
      ...element,
      patientName: element.patientName || this.patientFullName
    };

    const dialogData: BillDialogData = {
      billType: BillType.OPDBill,
      opdBillData: opdBillWithPatientName,
      selectedDoctorId: element.doctorId
    };

    this.dialog.open(AddBillChargeComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: ['bill-dialog', 'side-panel'],
      data: dialogData
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadPatientHistory();
      }
    });
  }

  listOfPatientReport(element: PatientAppointmentHistory) {
    this.dialog.open(PatientReportViewerComponent, {
      autoFocus: false,
      width: '90vw',
      height: 'auto',
      maxWidth: '100vh',
      maxHeight: '92vh',
      data: {
        appointmentDetails: element,
        isDialog: true
      }
    }).afterClosed().subscribe((result) => {
    });
  }

  OnDeleteAppointment(data: PatientAppointmentHistory) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.APPOINTMENT') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        const appointmentId = data?.id;
        if (appointmentId) {
          this._appointmentService.deleteAppointment(appointmentId).subscribe((response) => {
            if (response.isSuccess) {
              this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.APPOINTMENT')));
              this.loadPatientHistory();
            }
          });
        }
      }
    });
  }

  private loadPatientHistory(): void {
    const patientId = this._data.patientId || Number(this.patientId);
    this.isLoading = true;

    this.patientAppointmentHistoryService.getPatientHistory(patientId).subscribe({
      next: (response) => {
        this.dataSource.data = Array.isArray(response.result)
          ? response.result
          : [response.result];

        this.patientAppointmenthistoryLength.emit(this.dataSource.data.length);

        if (this.dataSource.data.length) {
          this._userService.getUserByID(this.dataSource.data[0]?.doctorId || 0).subscribe((res) => {
            this.doctorInfo = res.result;
          });
        }
        this.hospitalInfo = this.hospitalState.getHospital();
        this.checkReportsForHistory(this.dataSource.data);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading patient history:', err);
        this.isLoading = false;
      }
    });
  }
}
